import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Smartphone,
  BarChart3,
  TrendingUp,
  Bell,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  Calendar,
  Download,
  RefreshCw,
  LineChart,
  X,
  PieChart,
  Activity,
  ChevronDown // Added import
} from 'lucide-react';
import {
  LineChart as RechartsLineChart,
  BarChart as RechartsBarChart,
  PieChart as RechartsPieChart,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  Cell,
  Pie,
  Line,
} from 'recharts';
import GraphView from './GraphView';
import { apiFetch } from '../api';
import AnimatedBackground from './Background';
import StatNumberWithTooltip from './StatNumberWithTooltip';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';

// Main AppBasedStats Component
export default function AppBasedStats({ onBack, setError, API_BASE_URL, activeTab, loginId, deliveryMode }) {
  const { client, connected } = useWebSocket();
  const [appNames, setAppNames] = useState([]);
  const [selectedApp, setSelectedApp] = useState('');
  const [statsData, setStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAppNamesLoading, setIsAppNamesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('1d');
  const [showGraphView, setShowGraphView] = useState(false);
  const [selectedGraphType, setSelectedGraphType] = useState('daily-trend');

  // --- NEW: Auto Refresh State ---
  const [isLiveDataEnabled, setIsLiveDataEnabled] = useState(false); // Default OFF
  const [refreshInterval, setRefreshInterval] = useState(30); // Default 30 seconds
  const [showIntervalDropdown, setShowIntervalDropdown] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const dropdownRef = useRef(null);

  const intervalOptions = [
    { value: 15, label: '15 sec' },
    { value: 30, label: '30 sec' },
    { value: 45, label: '45 sec' },
    { value: 60, label: '1 min' },
    { value: 300, label: '5 min' },
  ];

  // 1. Define Configuration based on Active Tab
  let config = {
    apiNames: "",
    apiStats: "",
    wsNamesPub: "",
    wsNamesSub: "",
    wsStatsPub: "",
    wsStatsSub: ""
  };

  if (activeTab === 'email') {
    config.apiNames = "/getEmailAppName";
    config.apiStats = "/email/app_name/stats";
    config.wsNamesPub = "/app/getEmailAppName";
    config.wsNamesSub = `/topic/getEmailAppName/${loginId}`;
    config.wsStatsPub = "/app/email/app_name/stats";
    config.wsStatsSub = `/topic/email/app_name/stats/${loginId}`;
  } else if (activeTab === 'push') {
    config.apiNames = "/getAppName";
    config.apiStats = "/app_name/stats";
    config.wsNamesPub = "/app/getAppName";
    config.wsNamesSub = `/topic/getAppName/${loginId}`;
    config.wsStatsPub = "/app/app_name/stats";
    config.wsStatsSub = `/topic/app_name/stats/${loginId}`;
  } else if (activeTab === 'sms') {
    config.apiNames = "/getSmsAppName";
    config.apiStats = "/sms/app_name/stats";
    config.wsNamesPub = "/app/getSmsAppName";
    config.wsNamesSub = `/topic/getSmsAppName/${loginId}`;
    config.wsStatsPub = "/app/sms/app_name/stats";
    config.wsStatsSub = `/topic/sms/app_name/stats/${loginId}`;
  } else if (activeTab == 'whatsapp') {
    config.apiNames = "/whatsapp/getAppName";
    config.apiStats = "/whatsapp/app_name/stats";
    config.wsNamesPub = "/app/whatsapp/getAppName";
    config.wsNamesSub = `/topic/whatsapp/getAppName/${loginId}`;
    config.wsStatsPub = "/app/whatsapp/app_name/stats";
    config.wsStatsSub = `/topic/whatsapp/app_name/stats/${loginId}`;
  }
  else {
    config.apiNames = "/getQueueStatusMap";
  }

  // --- NEW: Helper Functions for Auto Refresh ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowIntervalDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let countdownInterval;
    if (isLiveDataEnabled && countdown > 0) {
      countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) return refreshInterval;
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [isLiveDataEnabled, countdown, refreshInterval]);

  const formatCountdown = (seconds) => {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  const toggleLiveData = () => {
    const newState = !isLiveDataEnabled;
    setIsLiveDataEnabled(newState);
    if (newState) {
      setCountdown(refreshInterval);
    } else {
      setCountdown(0);
    }
  };

  const handleIntervalChange = (value) => {
    setRefreshInterval(value);
    setShowIntervalDropdown(false);
    if (isLiveDataEnabled) {
      setCountdown(value);
    }
  };


  // 2. Fetch App Names (Fixed to remove duplicates)
  useEffect(() => {
    const fetchAppNames = async () => {
      setIsAppNamesLoading(true);
      setError('');

      try {
        if (deliveryMode === 'socket' && config.wsNamesPub) {
          try {
            await ensureSocketConnection(client);
          } catch (connError) {
            console.error(connError);
            setError('Socket connection failed.');
            setIsAppNamesLoading(false);
            return;
          }

          const subscription = client.subscribe(config.wsNamesSub, (msg) => {
            try {
              const payload = JSON.parse(msg.body);
              const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

              if (data && data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                // FIX: Use Set to remove duplicates from the backend response
                const rawNames = data.Response.map(item => item.appName).filter(Boolean);
                const uniqueNames = ['ALL', ...new Set(rawNames)];

                setAppNames(uniqueNames);

                if (uniqueNames.length > 1 && !selectedApp) {
                  setSelectedApp(uniqueNames[0]);
                }
              } else {
                setError('Failed to load app names via socket.');
              }
            } catch (e) {
              console.error('Failed to parse app names socket response', e);
            } finally {
              subscription.unsubscribe();
              setIsAppNamesLoading(false);
            }
          });

          client.publish({
            destination: config.wsNamesPub,
            body: JSON.stringify({})
          });

        } else {
          const response = await apiFetch(`${config.apiNames}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
            // FIX: Use Set to remove duplicates from the backend response
            const rawNames = data.Response.map(item => item.appName).filter(Boolean);
            const uniqueNames = ['ALL', ...new Set(rawNames)];

            setAppNames(uniqueNames);

            if (uniqueNames.length > 1 && !selectedApp) {
              setSelectedApp(uniqueNames[0]);
            }
          } else {
            setError('Failed to load app names. Invalid response format.');
          }
          setIsAppNamesLoading(false);
        }
      } catch (err) {
        console.error('Error fetching app names:', err);
        setError('Failed to load app names. Network error or API issue.');
        setIsAppNamesLoading(false);
      }
    };

    fetchAppNames();
  }, [activeTab, deliveryMode, API_BASE_URL, setError]);


  // 3. Trigger Stats Fetch when dependencies change
  // Note: We don't rely on this for auto-refresh, we use a separate interval effect
  useEffect(() => {
    if (selectedApp) {
      fetchAppStats(false); // Normal fetch, shows loader
    }
  }, [selectedApp, dateRange, activeTab, deliveryMode]);


  // --- NEW: Polling Effect ---
  useEffect(() => {
    let intervalId;
    if (isLiveDataEnabled && selectedApp) {
      intervalId = setInterval(() => {
        fetchAppStats(true); // Background fetch, NO loader
        setCountdown(refreshInterval);
      }, refreshInterval * 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLiveDataEnabled, refreshInterval, selectedApp, dateRange, activeTab, deliveryMode]);


  // 4. Fetch App Stats Function (Modified for silent refresh)
  const fetchAppStats = async (isBackground = false) => {
    if (!selectedApp) {
      console.warn('No app selected, skipping fetchAppStats');
      return;
    }

    if (!isBackground) setIsLoading(true);
    setError('');

    try {
      if (deliveryMode === 'socket' && config.wsStatsPub) {
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          // Only show error on UI if it's a manual fetch
          if (!isBackground) {
            setError('Socket connection failed.');
            setIsLoading(false);
          }
          return;
        }

        const subscription = client.subscribe(config.wsStatsSub, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const result = (typeof payload === 'string') ? JSON.parse(payload) : payload;
            if (result.STATUS === 'SUCCESS' && result.data) {
              const newStatsData = {
                appName: result.data.appName,
                totalNotifications: result.data.count,
                delivered: result.data.Delivered_Count,
                failed: result.data.Failed_Count || result.data.Falied_Count,
                pending: result.data.Pending_Count,
                deliveryRate: result.data.deliveryRate,
                dailyStats: result.data.dailyStats.map((stat) => ({
                  date: stat.date,
                  sent: stat.sent,
                  delivered: stat.Delivered_Count,
                  failed: stat.Failed_Count || stat.Falied_Count,
                })),
                topTopics: result.data.topTopics || [],
              };
              setStatsData(newStatsData);
            } else {
              if (!isBackground) setError('Failed to load app statistics via socket.');
            }
          } catch (e) {
            console.error('Failed to parse stats socket response', e);
          } finally {
            subscription.unsubscribe();
            if (!isBackground) setIsLoading(false);
          }
        });

        const payload = {
          appName: selectedApp,
          day_str: dateRange
        };

        client.publish({
          destination: config.wsStatsPub,
          body: JSON.stringify(payload)
        });

      } else {
        // --- API LOGIC ---
        const url = `${config.apiStats}?appName=${selectedApp}&day_str=${dateRange}`;
        const response = await apiFetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();

        if (result.STATUS === 'SUCCESS' && result.data) {
          const newStatsData = {
            appName: result.data.appName,
            totalNotifications: result.data.count,
            delivered: result.data.Delivered_Count,
            failed: result.data.Failed_Count || result.data.Falied_Count,
            pending: result.data.Pending_Count,
            deliveryRate: result.data.deliveryRate,
            dailyStats: result.data.dailyStats.map((stat) => ({
              date: stat.date,
              sent: stat.sent,
              delivered: stat.Delivered_Count,
              failed: stat.Failed_Count || stat.Falied_Count,
            })),
            topTopics: result.data.topTopics || [],
          };
          setStatsData(newStatsData);
        } else {
          if (!isBackground) setError('Failed to load app statistics.');
        }
        if (!isBackground) setIsLoading(false);
      }
    } catch (err) {
      console.error('Error fetching app stats:', err);
      if (!isBackground) {
        setError('Failed to load app statistics. Network error or API issue.');
        setIsLoading(false);
      }
    }
  };

  if (showGraphView) {
    return (
      <GraphView
        onBack={() => setShowGraphView(false)}
        statsData={statsData}
        selectedApp={selectedApp}
        selectedGraphType={selectedGraphType}
        setSelectedGraphType={setSelectedGraphType}
        activeTab={activeTab}
      />
    );
  }

  const filteredApps = appNames.filter((app) =>
    app.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDeliveryRateColor = (rate) => {
    if (rate >= 0.9) return 'text-green-400';
    if (rate >= 0.7) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getDeliveryRateBackground = (rate) => {
    if (rate >= 0.9) return 'bg-green-500/10 border-green-500/30';
    if (rate >= 0.7) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-red-500/10 border-red-500/30';
  };

  const getOverviewPieData = () => {
    if (!statsData) return [];
    return [
      { name: 'Delivered', value: statsData.delivered, color: '#10B981' },
      { name: 'Failed', value: statsData.failed, color: '#EF4444' },
      { name: 'Pending', value: statsData.pending, color: '#F59E0B' },
    ];
  };

  const getTopicsBarData = () => {
    if (!statsData) return [];
    return statsData.topTopics.map((topic) => ({
      name: topic.name.replace('_', ' ').toUpperCase(),
      count: topic.count,
    }));
  };

  const getProgressBarWidth = (value) => {
    if (!statsData || statsData.totalNotifications === 0) {
      return '0%';
    }
    return `${(value / statsData.totalNotifications) * 100}%`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8] p-3 sm:p-6 font-sans">
      <div className="max-w-7xl mx-auto">

        {/* Header - Mobile Responsive: flex-col on mobile, row on desktop */}
        <div className="relative z-50 flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6 xl:mb-8">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <button
              onClick={onBack}
              className="p-2 sm:p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white backdrop-blur-sm border border-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">App-Based Analytics</h1>
                <p className="text-white/60 text-xs sm:text-base">Detailed statistics for each application</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">

            {/* --- NEW: Auto Refresh Controls --- */}
            {deliveryMode === 'socket' && (
              <div className="flex items-center space-x-2 bg-white/5 backdrop-blur-md border border-white/20 rounded-xl px-3 py-2">
                <span className={`text-xs font-medium transition-colors duration-300 ${isLiveDataEnabled ? 'text-white' : 'text-white/50'}`}>
                  Live
                </span>
                <button
                  onClick={toggleLiveData}
                  className={`relative w-11 h-6 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${isLiveDataEnabled
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                    : 'bg-gray-600'
                    }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-lg transform transition-all duration-300 flex items-center justify-center ${isLiveDataEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isLiveDataEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                  </span>
                </button>

                {isLiveDataEnabled && countdown > 0 && (
                  <div className="flex items-center space-x-1 bg-green-500/10 px-2 py-1 rounded">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-xs text-green-400 font-mono font-semibold min-w-[32px]">
                      {formatCountdown(countdown)}
                    </span>
                  </div>
                )}

                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowIntervalDropdown(!showIntervalDropdown)}
                    className="flex items-center space-x-1 px-2 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 rounded text-xs transition-all duration-200"
                  >
                    <span>{intervalOptions.find(opt => opt.value === refreshInterval)?.label}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>

                  {showIntervalDropdown && (
                    <div className="absolute right-0 mt-2 min-w-[6rem] bg-slate-900 border border-white/20 rounded-lg shadow-xl overflow-hidden z-[100]">
                      {intervalOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleIntervalChange(option.value)}
                          className={`w-full px-3 py-2 text-left text-xs hover:bg-purple-500/20 transition-colors block ${refreshInterval === option.value
                            ? 'bg-purple-500/30 text-purple-200'
                            : 'text-white/70'
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => setShowGraphView(true)}
              disabled={!statsData}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm sm:text-base"
            >
              <LineChart className="w-4 h-4" />
              <span className="hidden sm:inline">Show Graph View</span>
              <span className="sm:hidden">Graph</span>
            </button>
            <button
              onClick={() => fetchAppStats(false)}
              disabled={isLoading || !selectedApp}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500/20 text-blue-200 rounded-xl hover:bg-blue-500/30 transition-all duration-200 disabled:opacity-50 text-sm sm:text-base"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">

          {/* Left Column: App Selection */}
          <div className="lg:col-span-3">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 lg:h-[calc(100vh-12rem)] h-96 flex flex-col">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Smartphone className="w-5 h-5 mr-2 text-purple-400" />
                Select App
              </h3>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search apps..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300"
                />
              </div>

              {isAppNamesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                  <span className="ml-2 text-white/60">Loading apps...</span>
                </div>
              ) : filteredApps.length > 0 ? (
                <div className="space-y-2 flex-grow overflow-y-auto custom-scrollbar">
                  {filteredApps.map((app) => (
                    <button
                      key={app}
                      onClick={() => setSelectedApp(app)}
                      className={`w-full text-left p-3 rounded-xl transition-all duration-200 ${selectedApp === app
                        ? 'bg-blue-500/20 border border-blue-500/50 text-blue-200'
                        : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-transparent'
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedApp === app ? 'bg-blue-500/30' : 'bg-white/10'
                            }`}
                        >
                          <Smartphone className="w-4 h-4" />
                        </div>
                        <span className="font-medium truncate text-sm">{app}</span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-white/60 flex-grow flex flex-col items-center justify-center">
                  <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No apps found</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Time Range & Stats */}
          <div className="lg:col-span-9 flex flex-col space-y-4 sm:space-y-6">

            {/* Time Range Filter - Scrollable on mobile */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Filter className="w-5 h-5 mr-2 text-green-400" />
                Time Range
              </h3>

              <div className="flex overflow-x-auto pb-2 sm:pb-0 gap-3 scrollbar-hide sm:grid sm:grid-cols-5">
                {[
                  { value: '1d', label: 'Today' },
                  { value: '7d', label: '7 Days' },
                  { value: '30d', label: '30 Days' },
                  { value: '90d', label: '90 Days' },
                  { value: 'all', label: '120 Days' },
                ].map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setDateRange(range.value)}
                    className={`whitespace-nowrap px-4 py-3 min-w-[100px] rounded-xl transition-all duration-200 flex items-center justify-center space-x-2 ${dateRange === range.value
                      ? 'bg-green-500/20 border border-green-500/50 text-green-200'
                      : 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-transparent'
                      }`}
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium text-sm">{range.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Statistics Display */}
            {selectedApp && (
              <div className="flex-grow">
                {isLoading ? (
                  <div className="flex items-center justify-center py-20 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl h-64">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
                      <p className="text-lg text-white">Loading statistics...</p>
                      <p className="text-sm text-white/60">Analyzing data for {selectedApp}</p>
                    </div>
                  </div>
                ) : statsData ? (
                  <div className="space-y-6">
                    {/* Overview Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                            <Bell className="w-6 h-6 text-blue-400" />
                          </div>
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-500/20 text-blue-300">
                            Total
                          </span>
                        </div>
                        <h3 className="text-white/80 text-sm font-medium mb-1">Total Sent</h3>
                        <StatNumberWithTooltip value={statsData.totalNotifications} />
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div className="h-2 bg-blue-500 rounded-full w-full"></div>
                        </div>
                      </div>

                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-400" />
                          </div>
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-500/20 text-green-300">
                            Success
                          </span>
                        </div>
                        <h3 className="text-white/80 text-sm font-medium mb-1">Delivered</h3>
                        <StatNumberWithTooltip value={statsData.delivered} />
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div
                            className="h-2 bg-green-500 rounded-full transition-all duration-1000"
                            style={{ width: getProgressBarWidth(statsData.delivered) }}
                          ></div>
                        </div>
                      </div>

                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center">
                            <XCircle className="w-6 h-6 text-red-400" />
                          </div>
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-500/20 text-red-300">
                            Failed
                          </span>
                        </div>
                        <h3 className="text-white/80 text-sm font-medium mb-1">Failed</h3>
                        <StatNumberWithTooltip value={statsData.failed} />
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div
                            className="h-2 bg-red-500 rounded-full transition-all duration-1000"
                            style={{ width: getProgressBarWidth(statsData.failed) }}
                          ></div>
                        </div>
                      </div>

                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
                        <div className="flex items-center justify-between mb-4">
                          <div className="w-12 h-12 bg-yellow-500/20 rounded-2xl flex items-center justify-center">
                            <Clock className="w-6 h-6 text-yellow-400" />
                          </div>
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-300">
                            Pending
                          </span>
                        </div>
                        <h3 className="text-white/80 text-sm font-medium mb-1">Pending</h3>
                        <StatNumberWithTooltip value={statsData.pending} />
                        <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                          <div
                            className="h-2 bg-yellow-500 rounded-full transition-all duration-1000"
                            style={{ width: getProgressBarWidth(statsData.pending) }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Rate Card */}
                    <div
                      className={`p-6 rounded-2xl border backdrop-blur-xl ${getDeliveryRateBackground(
                        statsData.deliveryRate
                      )}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">Overall Delivery Rate</h3>
                            <p className="text-white/60 text-sm">Success rate for {selectedApp}</p>
                          </div>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className={`text-4xl font-bold ${getDeliveryRateColor(statsData.deliveryRate)}`}>
                            {(statsData.deliveryRate * 100).toFixed(1)}%
                          </p>
                          <p className="text-white/60 text-sm">
                            {statsData.deliveryRate >= 0.9
                              ? 'Excellent'
                              : statsData.deliveryRate >= 0.7
                                ? 'Good'
                                : 'Needs Improvement'}
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all duration-1000 ${statsData.deliveryRate >= 0.9
                            ? 'bg-green-500'
                            : statsData.deliveryRate >= 0.7
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                            }`}
                          style={{ width: `${statsData.deliveryRate * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Daily Trend Chart */}
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                          <Activity className="w-5 h-5 mr-2 text-blue-400" />
                          Daily Notification Trend
                        </h3>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={statsData.dailyStats}>
                              <defs>
                                <linearGradient id="colorSentMini" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorDeliveredMini" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorFailedMini" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                              <XAxis dataKey="date" stroke="#9CA3AF" />
                              <YAxis stroke="#9CA3AF" />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: '#1F2937',
                                  border: '1px solid #374151',
                                  borderRadius: '8px',
                                  color: '#F9FAFB',
                                }}
                              />
                              <Legend />
                              <Area
                                type="monotone"
                                dataKey="sent"
                                stroke="#3B82F6"
                                fillOpacity={1}
                                fill="url(#colorSentMini)"
                                name="Sent"
                              />
                              <Area
                                type="monotone"
                                dataKey="delivered"
                                stroke="#10B981"
                                fillOpacity={1}
                                fill="url(#colorDeliveredMini)"
                                name="Delivered"
                              />
                              <Area
                                type="monotone"
                                dataKey="failed"
                                stroke="#EF4444"
                                fillOpacity={1}
                                fill="url(#colorFailedMini)"
                                name="Failed"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Notification Status Distribution */}
                      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                          <PieChart className="w-5 h-5 mr-2 text-pink-400" />
                          Status Distribution
                        </h3>
                        <div className="h-64 flex items-center justify-center">
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={getOverviewPieData()}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {getOverviewPieData().map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: '#1F2937',
                                  border: '1px solid #374151',
                                  borderRadius: '8px',
                                  color: '#F9FAFB',
                                }}
                              />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Top Topics Chart */}
                      {activeTab === 'push' && (
                        <div className="lg:col-span-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                            <BarChart3 className="w-5 h-5 mr-2 text-orange-400" />
                            Top Notification Topics
                          </h3>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsBarChart data={getTopicsBarData()}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="name" stroke="#9CA3AF" />
                                <YAxis stroke="#9CA3AF" />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: '#1F2937',
                                    border: '1px solid #374151',
                                    borderRadius: '8px',
                                    color: '#F9FAFB',
                                  }}
                                />
                                <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                              </RechartsBarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                ) : (
                  <div className="text-center py-20 text-white/60 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl h-full flex flex-col items-center justify-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Select an application to view its statistics.</p>
                    <p className="text-sm">Data will appear here after selection.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}